import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { Skeleton } from "antd";

import ManufacturerDetailsTab from "./ManufacturerDetailsTab";
import ManufacturerInventoryTab from "./ManufacturerInventoryTab";
import ManufacturerContractsDashboard from "../ManufacturerCommissionContract/ManufacturerContractsDashboard";
import AABIManufacturerTab from "../AABI/AABIManufacturerTab";

import APIService from "../../Service/APIService";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import TabFade from "../../common/tab-fade";
import "./Manufacturers.css";

const ManufacturerDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const userData = user;
  const isCreateMode = !id;
  const [manufacturerDetails, setManufacturerDetails] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const requestedTab = queryParams.get("tab");
  const manufacturerNameFromQuery = (queryParams.get("manufacturerName") || "").trim();
  const defaultSegment =
    isCreateMode || requestedTab === "details"
      ? "Manufacturer Details"
      : requestedTab === "inventory"
        ? "Inventory Page"
        : "Manufacturer Home";

  const [selectedSegment, setSelectedSegment] = useState(defaultSegment);

  useEffect(() => {
    setSelectedSegment(defaultSegment);
  }, [defaultSegment]);

  /**
   * Auth + fetch
   */
  const fetchManufacturerDetails = useCallback(async () => {
    if (isCreateMode) {
      const createDefaults = {
        ManufacturerName: manufacturerNameFromQuery,
        IsActive: true,
        NeedsApproval: true,
        ASInvoice: false,
        CollectPayment: false,
        ManufacturerVisibilityDO: false,
        ManufacturerVisibilityCO: false,
        ManufacturerVisibilityPU: false,
      };
      setManufacturerDetails(createDefaults);
      return createDefaults;
    }
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend(
      `getmanufacturerbyid?ID=${id}`
    );
    setManufacturerDetails(response?.data?.data);
    return response?.data?.data;
  }, [id, isCreateMode, manufacturerNameFromQuery]);

  useEffect(() => {
    if (userData?.Type === "Account") {
      window.location.href = "/";
      return;
    }

    fetchManufacturerDetails();
  }, [fetchManufacturerDetails, userData?.Type]);

  const entityNameValue = useMemo(() => {
    if (!manufacturerDetails) {
      return (
        <Skeleton.Input active style={{ width: 60, height: 16 }} />
      );
    }
    return "Manufacturers";
  }, [manufacturerDetails]);

  const labelValue = useMemo(() => {
    if (!manufacturerDetails) {
      return (
        <Skeleton.Input active style={{ width: 260, height: 20 }} />
      );
    }
    return manufacturerDetails.ManufacturerName;
  }, [manufacturerDetails]);

  /**
   * Handlers
   */
  const handleSegmentChange = (value) => {
    setSelectedSegment(value);
  };

  // D6: Manufacturer users may only view their own manufacturer. Gated here
  // (rather than inside a single tab) so every tab on the page is covered.
  if (
    !isCreateMode &&
    userData?.Type === "Manufacturer" &&
    String(id) !== String(userData?.ManufacturerID)
  ) {
    return <Navigate to={`/Manufacturer/${userData.ManufacturerID}`} replace />;
  }

  return (
    <div>
      <TitleBar
        entityName={entityNameValue}
        entityNameLink="/ManufacturerDashboard"
        label={labelValue}
        statusIndicatorStatus={
          manufacturerDetails
            ? manufacturerDetails.IsActive
              ? "Active"
              : "Inactive"
            : ""
        }
        statusIndicatorStatusType={
          manufacturerDetails
            ? manufacturerDetails.IsActive
              ? "success"
              : "failure"
            : ""
        }
        filterBarComponents={
          <>
            {!isCreateMode && (
              <Button
                isActive={selectedSegment === "Manufacturer Home"}
                variant="timeframe"
                onClick={() => handleSegmentChange("Manufacturer Home")}
              >
                Home
              </Button>
            )}
            <Button
              isActive={selectedSegment === "Manufacturer Details"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Manufacturer Details")}
            >
              Details
            </Button>
            {!isCreateMode && (
              <Button
                isActive={selectedSegment === "Inventory Page"}
                variant="timeframe"
                onClick={() => handleSegmentChange("Inventory Page")}
              >
                Inventory Page
              </Button>
            )}

            {!isCreateMode &&
              (userData?.Type === "Admin" ||
                userData?.Type === "ASD" ||
                userData?.Type === "Authorizer" ||
                userData?.Type === "Viewer") &&
              hasPermission(userData, "mfr_contract.view") && (
                <Button
                  isActive={selectedSegment === "Manufacturer Contracts"}
                  variant="timeframe"
                  onClick={() =>
                    handleSegmentChange("Manufacturer Contracts")
                  }
                >
                  Manufacturer Contracts
                </Button>
              )}
          </>
        }
        // Analytics controls live in the in-content AnalyticsToolbar (Stage 1
        // UI upgrade §5.2) — rightFilterBarComponents is intentionally empty.
        rightFilterBarComponents={null}
      />

      <TabFade activeKey={selectedSegment} style={{ marginTop: "20px" }}>
        {selectedSegment === "Manufacturer Home" && (
          <AABIManufacturerTab
            manufacturerId={id}
            manufacturerName={manufacturerDetails?.ManufacturerName}
          />
        )}

        {selectedSegment === "Manufacturer Details" && (
          <ManufacturerDetailsTab
            manufacturerDetails={manufacturerDetails}
            onManufacturerUpdated={fetchManufacturerDetails}
          />
        )}

        {selectedSegment === "Inventory Page" && (
          <ManufacturerInventoryTab />
        )}

        {(userData?.Type === "Admin" ||
          userData?.Type === "ASD" ||
          userData?.Type === "Authorizer" ||
          userData?.Type === "Viewer") &&
          hasPermission(userData, "mfr_contract.view") &&
          selectedSegment === "Manufacturer Contracts" && (
            <ManufacturerContractsDashboard />
          )}
      </TabFade>
    </div>
  );
};

export default ManufacturerDetails;
